// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { brandStandards } from "@guestsupply/XDL/constants";
import Avendra from "@guestsupply/XDL/Icons/Avendra";
import AvendraItem from "@guestsupply/XDL/Icons/AvendraItem";
import BrandApprovedSubstituteBadge from "@guestsupply/XDL/Icons/BrandApprovedSubstituteBadge";
import BrandMandateBadge from "@guestsupply/XDL/Icons/BrandMandateBadge";
import BrandPreferredProductBadge from "@guestsupply/XDL/Icons/BrandPreferredProductBadge";
import GuestSupplyRecommendedBadge from "@guestsupply/XDL/Icons/GuestSupplyRecommendedBadge";
import MeetsBrandStandardsBadge from "@guestsupply/XDL/Icons/MeetsBrandStandardsBadge";
import { XProductModel } from "@guestsupply/XDL/types";
import { parseBoolean } from "@guestsupply/XDL/Utilities";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import {
    BadgeImagePlacementValues,
    BadgeModel,
    BadgeStyleValues,
    BadgeTypeValues,
} from "@insite/client-framework/Types/ApiModels";

interface Props {
    productContext: ProductContextModel;
    showTextBadges?: boolean;
    showImageBadges?: boolean;
    showPreviouslyPurchased?: boolean;
    showBrandStandardName?: boolean;
    showIsAvendra?: boolean;
    isPdp?: boolean;
}

const previouslyPurchasedTagId = "previouslyPurchased";
const brandStandardNameId = "brandStandardName";
const avendraId = "avendra";

const defaultTag = {
    badgeColorHexCode: "646569",
    badgeType: BadgeTypeValues.Text,
    badgeStyle: BadgeStyleValues.Rectangle,
    displayOnBadgeWidget: true,
    displayOnProductImages: true,
    id: "",
    imageAltText: "",
    largeImageBadgePath: "",
    largeImagePlacement: BadgeImagePlacementValues.None,
    name: "",
    tagName: "",
    sortOrder: -1,
    textColorHexCode: "",
} as BadgeModel;

const sortBadges = (badges: BadgeModel[] | null) => badges?.sort((a, b) => a.sortOrder - b.sortOrder) || [];

const createImageBadge = (id: string, image: any, altText: string) => ({
    ...defaultTag,
    badgeType: BadgeTypeValues.Image,
    id,
    imageAltText: altText,
    largeImageBadgePath: image,
    name: altText,
    tagName: altText,
});

const xUseWidgetBadges = ({
    productContext,
    showTextBadges,
    showImageBadges,
    showPreviouslyPurchased = false,
    showBrandStandardName = true,
    showIsAvendra = true,
    isPdp = false,
}: Props) => {
    const { textBadges, imageBadges } = useWidgetBadges({ productContext, showImageBadges, showTextBadges });

    const ootbBadgesAndTags = sortBadges([...(textBadges || []), ...(imageBadges || [])]);
    const customBadgesAndTags = [];

    const { properties, previouslyPurchased } = productContext.product as XProductModel;
    const brandStandardName = properties.brandStandardName;

    if (showBrandStandardName && brandStandardName && showImageBadges) {
        const brandBadgeMap = {
            [brandStandards.meetsBrandStandards]: MeetsBrandStandardsBadge,
            [brandStandards.brandMandate]: BrandMandateBadge,
            [brandStandards.guestSupplyRecommended]: GuestSupplyRecommendedBadge,
            [brandStandards.brandApprovedSubstitute]: BrandApprovedSubstituteBadge,
            [brandStandards.brandPreferredProduct]: BrandPreferredProductBadge,
        };
        customBadgesAndTags.unshift(
            createImageBadge(brandStandardNameId, brandBadgeMap[brandStandardName] || "", brandStandardName),
        );
    }

    if (showPreviouslyPurchased && previouslyPurchased && showTextBadges) {
        customBadgesAndTags.unshift({
            ...defaultTag,
            displayText: translate("Prev. Purchase"),
            id: previouslyPurchasedTagId,
            name: translate("Previously Purchased"),
            tagName: translate("Previously Purchased"),
        });
    }

    if (showIsAvendra && parseBoolean(properties.isAvendra) && showImageBadges) {
        const avendraMessage = siteMessage(
            "GUEST_Avendra_Pricing_Icon_Hover",
            "This product pricing is specific to the Avendra contract",
        ) as string;
        customBadgesAndTags.unshift(createImageBadge(avendraId, isPdp ? AvendraItem : Avendra, avendraMessage));
    }

    return {
        ootbBadgesAndTags,
        customBadgesAndTags,
    };
};

export default xUseWidgetBadges;
